import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { ActivityCodeEnum, PurposeEnum } from 'src/app/models/shared-models';
import { SessionService } from 'src/app/services/session.service';
import { environment } from 'src/environments/environment';
import { IActivityStorageDataModel, IExchangeAreaDataModel, IExchangeAreaInformation, ILotPlanModel, IPurposeDataModel } from '../models/veg-wizard-models';
import { Logger } from './logger-service';

@Injectable({
  providedIn: 'root'
})
export class ExchangeAreaService {
  constructor(
    private http: HttpClient,
    private logger: Logger,
    private sessionService: SessionService,
  ) {}

  setSessionExchangeArea$(model: IExchangeAreaDataModel, sessionId: string | undefined): Observable<boolean> {

    return this.http.post<boolean>(
      `${environment.apiUrl}/session/set-exchange-area`, { ...model, sessionKey: sessionId })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.logger.error('setExchangeArea error', err);
          return of(false)
        }),
        map(() => true) // return true, as it should be HttpStatusCode OK
      );
  }

  getExchangeAreaInformation(activity: IActivityStorageDataModel, purpose: IPurposeDataModel): IExchangeAreaInformation | null {
    const activityCode = activity ? activity.activity : '';

    let purposeCode = '';

    if (purpose != null) {
      purposeCode = purpose.purpose?.purposeCode;
    }

    const exchangeAreaInformation: IExchangeAreaInformation | null = this.checkForMessageByPurpose(purposeCode) || this.checkForMessageByActivity(activityCode);

    return exchangeAreaInformation || ({} as IExchangeAreaInformation);
  }

  determineToShowKoalaHabitatProtectionMessage(lotOnPlan: ILotPlanModel, activity : IActivityStorageDataModel, purpose: IPurposeDataModel): boolean {

    const activityCode = activity ? activity.activity : '';

    let purposeCode = '';

    if (purpose != null) {
      purposeCode = purpose.purpose?.purposeCode;
    }

    if (lotOnPlan != null) {
      if (lotOnPlan.containsKoalaHabitatArea || lotOnPlan.containsKoalaPriorityArea || lotOnPlan.containsLocallyRefinedKoalaHabitatArea || lotOnPlan.containsSeqRegionalPlanArea) {
        if (purposeCode === PurposeEnum.infrastructure) {
          if (activityCode === ActivityCodeEnum.airstripOrHelipads ||
            activityCode === ActivityCodeEnum.boundaryFences ||
            activityCode === ActivityCodeEnum.contourBanks ||
            activityCode === ActivityCodeEnum.drainageAndErosionManagement ||
            activityCode === ActivityCodeEnum.fireManagementLines ||
            activityCode === ActivityCodeEnum.firebreaks ||
            activityCode === ActivityCodeEnum.internalFences ||
            activityCode === ActivityCodeEnum.irrigationChannels ||
            activityCode === ActivityCodeEnum.otherLinearInfrastructure ||
            activityCode === ActivityCodeEnum.otherNonLinearInfrastructure ||
            activityCode === ActivityCodeEnum.tracks) {
            return true;
          }
        }

        if (purposeCode === PurposeEnum.extractiveIndustry) {
          return true;
        }

        if (purposeCode === PurposeEnum.necessaryEnvironmentalClearing) {
          if (activityCode === ActivityCodeEnum.channelDiversion) {
            return true;
          }
        }
      }
    }

    return false;
  }

  checkForMessageByActivity(activityCode: string): IExchangeAreaInformation | null {
    let exchangeAreaInformation: IExchangeAreaInformation | null = {} as IExchangeAreaInformation;

    switch (activityCode) {
      case ActivityCodeEnum.contaminantRemoval:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds the limits of the code or',
          'is within a riparian protection zone and exceeds the limits of the code.'
        ];
        break;
      case ActivityCodeEnum.channelDiversion:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds the width limits of the code.'
        ];
        break;
      case ActivityCodeEnum.internalFences:
      case ActivityCodeEnum.tracks:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds 10 metres in width or',
          'will result in the widening of an existing cleared area to more than 10 metres in width or',
          'is within 10 metres of the defining bank of a wetland or',
          'is within 10 metres of a defining bank of a watercourse or drainage feature unless a crossing is required or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.contourBanks:
      case ActivityCodeEnum.irrigationChannels:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds 10 metres in width or',
          'will result in the widening of an existing cleared area to more than 10 metres in width or',
          'is within 10 metres of the defining bank of a wetland or',
          'is within 10 metres of a defining bank of a watercourse or drainage feature or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.boundaryFences:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds 10 metres in width of the landholder’s side of the property boundary or',
          'will result in the widening of an existing cleared area on the landholder’s side of the property to more than 10 metres in width or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.airstripOrHelipads:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'occurs within a riparian protection zone or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.otherLinearInfrastructure:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds the width limits in the code or',
          'widens the existing cleared area to more than the width limits in the code or',
          'is within 10 metres of the defining bank of a wetland or',
          'is within 10 metres of a defining bank of a watercourse or drainage feature unless a crossing of the watercourse or drainage feature is required or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.otherNonLinearInfrastructure:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when:';
        exchangeAreaInformation.questions = [
          'exceeds the area limits of the code or',
          'is within an essential habitat area or',
          'is in a riparian protection zone or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.fireManagementLines:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds 10 metres in width or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.firebreaks:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'exceeds 30 metres in width or',
          'widens an existing firebreak to more than 30 metres in width or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      case ActivityCodeEnum.drainageAndErosionManagement:
        exchangeAreaInformation.header =
          'When clearing Category C and/or Category R areas an exchange area is required when the clearing:';
        exchangeAreaInformation.questions = [
          'is greater than 900m' + `<sup><small>2</small></sup>` + ' or',
          'is within 100 metres of a salinity expression area.'
        ];
        break;
      default:
        exchangeAreaInformation = null;
        break;
    }

    return exchangeAreaInformation;
  }

  checkForMessageByPurpose(purposeCode: string): IExchangeAreaInformation | null {
    switch (purposeCode) {
      case PurposeEnum.fodder:
        return {
          header:
            'When clearing Category C and/or Category R areas an exchange area is required when the clearing:',
          questions: ['is in an essential habitat area.']
        };
      case PurposeEnum.publicSafety:
        return {
          header:
            'When clearing Category C and/or Category R areas an exchange area is required when the clearing:',
          questions: [
            'occurs within an endangered and/or of concern regional ecosystem and exceeds the limits of the code or',
            'occurs within an essential habitat area and exceeds the limits in the code or',
            'occurs within a riparian protection zone or',
            'occurs in an area which provides the only connection between two or more areas which are a Category B area, Category A area, wetland or essential habitat area' +
              ' where the vegetation is less than 100 metres in width or will reduce the width of vegetation to less than 100 metres or',
            'is within 100 metres of a salinity expression area.'
          ]
        };
      case PurposeEnum.coordinatedProjects:
        return {
          header:
            'When clearing Category C and/or Category R areas an exchange area is required when the clearing:',
          questions: [
            'occurs within endangered and/or of concern regional ecosystems and exceeds the limits in the code or',
            'occurs within an essential habitat area and exceeds the limits in the code or',
            'occurs within a riparian protection zone or',
            'occurs in an area which provides the only connection between two or more areas which are a Category B area, Category A area, wetland or essential habitat area' +
              ' where the vegetation is less than 100 metres in width or will reduce the width of vegetation to less than 100 metres or',
            'is within 100 metres of a salinity expression area.'
          ]
        };
      case PurposeEnum.extractiveIndustry:
        return {
          header:
            'When clearing Category C and/or Category R areas an exchange area is required when the clearing:',
          questions: [
            'is for a pit or quarry and occurs within an endangered and/or of concern regional ecosystem and exceeds the limits in the code or',
            'is for a pit or quarry and occurs within an essential habitat area and exceeds the limits of the code or',
            'exceeds 10 hectares of category B areas, category C areas and category R areas',
            'is for a pit or quarry and occurs within a riparian protection zone or',
            'is for a road or vehicular track which exceeds 10 metres in width or increases the width of the existing cleared area to more than 10 metres or',
            'is for a road or vehicular track and is within 10 metres of a defining bank of a wetland or',
            'is for a road or vehicular track and is within 10 metres of the defining bank of a watercourse or drainage feature unless a crossing of the watercourse or drainage feature is required or',
            'is within 100 metres of a salinity expression area.'
          ]
        };
      default:
        return null;
    }
  }
}
